import api from './api';

export interface InheritanceClaimSummary {
  id: string;
  claimNumber: string;
  deceasedAccountId: string;
  submittedByName: string;
  submittedByMemberId: string;
  status: 'PENDING' | 'UNDER_REVIEW' | 'APPROVED' | 'PAID' | 'REJECTED';
  balanceAtApproval?: string | null;
  payoutBatchId?: string | null;
  createdAt: string;
  user?: { firstName: string; lastName: string; email: string };
}

export interface WillBeneficiaryShare {
  id: string;
  percentage: number;
  familyMember: {
    memberId: string;
    firstName: string;
    lastName: string | null;
    relationship: string;
    passportNumber: string | null;
    email: string | null;
    isDeceased: boolean;
  };
}

export interface InheritanceClaimDetail extends InheritanceClaimSummary {
  deceasedUserId: string;
  deathCertificateUrl: string;
  submittedByNric: string;
  submittedByEmail?: string | null;
  submittedByPhone?: string | null;
  relationship?: string | null;
  bankDetails?: Record<string, string> | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
  rejectionReason?: string | null;
  user: {
    id: string;
    memberId: string;
    firstName: string;
    lastName: string;
    email: string;
    willUnlockedAt: string | null;
    wallet: {
      walletNumber: string;
      balance: string;
      currency: string;
      isFrozen: boolean;
      freezeReason: string | null;
    } | null;
  };
  beneficiaries: WillBeneficiaryShare[];
  payoutBatch?: {
    id: string;
    batchNumber: string;
    status: string;
    totalAmount: string;
    itemCount: number;
  } | null;
}

export interface ClaimStatusResponse {
  claimNumber: string;
  status: string;
  submittedAt: string;
}

export const inheritanceClaimsService = {
  /** Public: submit a claim (multipart with the death certificate). */
  async submit(
    fields: Record<string, string>,
    deathCertificate: File,
  ): Promise<{ claimNumber: string; status: string; createdAt: string }> {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      if (value) formData.append(key, value);
    });
    formData.append('deathCertificate', deathCertificate);
    const response = await api.post('/inheritance-claims', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /** Public: check a claim status (requires the claim number + NRIC). */
  async getStatus(claimNumber: string, nric: string): Promise<ClaimStatusResponse> {
    const response = await api.get<ClaimStatusResponse>('/inheritance-claims/status', {
      params: { claimNumber, nric },
    });
    return response.data;
  },

  /** Admin: queue list. */
  async list(status?: string): Promise<{
    claims: InheritanceClaimSummary[];
    counts: Record<string, number>;
  }> {
    const response = await api.get('/inheritance-claims', {
      params: status ? { status } : undefined,
    });
    return response.data;
  },

  /** Admin: full claim detail. */
  async getDetail(id: string): Promise<InheritanceClaimDetail> {
    const response = await api.get<InheritanceClaimDetail>(`/inheritance-claims/${id}`);
    return response.data;
  },

  /** Admin: review (UNDER_REVIEW / APPROVE / REJECT + reason). */
  async review(
    id: string,
    action: 'UNDER_REVIEW' | 'APPROVE' | 'REJECT',
    payload: { reviewNotes?: string; rejectionReason?: string } = {},
  ): Promise<InheritanceClaimDetail> {
    const response = await api.patch<InheritanceClaimDetail>(
      `/inheritance-claims/${id}/review`,
      { action, ...payload },
    );
    return response.data;
  },
};
